import React, { useContext, useEffect, useRef, useState } from 'react';
import { SocketContext } from '../../contexts/SocketContext';
import { WebRTCContext } from '../../contexts/WebRTCContext';
import RecordingCard from './RecordingCard';
import { Alert, AspectRatio, Grid, Sheet, Snackbar, Typography } from "@mui/joy";
import DaysContainer from "./CalendarWidget";
import VideoPlayer from "./videoplayer/VideoPlayer";
import SessionCard from "./sessions/SessionCard";

const RecordingsTab = () => {
    const { socket, isConnected, apiHandler } = useContext(SocketContext);
    const { rtcSession } = useContext(WebRTCContext);
    // Ref for the VideoPlayer component instance
    const videoPlayerRef = useRef(null);

    const [devices, setDevices] = useState([]);
    const [recordings, setRecordings] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDeviceId, setSelectedDeviceId] = useState(null);
    const [selectedSessions, setSelectedSessions] = useState(null);
    const [selectedRecordingId, setSelectedRecordingId] = useState(null);
    const [openMenu, setOpenMenu] = useState(null);
    const snackbarRef = useRef(null);
    const alertRef = useRef(null);

    useEffect(() => {
        if (rtcSession && rtcSession.remoteStream) {
            // The VideoPlayer component already handles assigning the remote stream.
        }
    }, [rtcSession]);

    useEffect(() => {
        if (isConnected && apiHandler && devices.length === 0) {
            loadDevices(); // Load devices when connected
        }
    }, [isConnected, apiHandler, devices.length]);

    useEffect(() => {
        const handleScroll = () => {
            setOpenMenu(null);
        };

        const container = document.getElementById('recordingsContainer');
        container?.addEventListener('scroll', handleScroll);

        return () => {
            container?.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const loadDevices = () => {
        if (apiHandler) {
            setLoading(true);
            const message = {
                method: 'api',
                type: 'get_all_devices',
                payload: { scope: 'full' },
            };

            apiHandler.sendRequest(message, {
                on_success: (data) => {
                    setDevices(data.devices);
                    if (data.devices.length > 0) {
                        const firstDevice = data.devices[0];
                        handleDeviceSelect(firstDevice.id);
                    }
                    setLoading(false);
                },
                on_failure: (error) => {
                    console.error("Request failed:", error);
                    setLoading(false);
                },
            });
        }
    };

    const loadRecordings = (deviceId) => {
        if (apiHandler) {
            setLoading(true);
            const utcMillisNow = Date.now();
            const message = {
                method: 'api',
                type: 'get_device_recordings',
                payload: {
                    device_id: deviceId,
                    from_ms: 0,
                    to_ms: utcMillisNow,
                    limit: 20,
                },
            };

            apiHandler.sendRequest(message, {
                on_success: (payload) => {
                    setRecordings(payload.recordings);
                    setLoading(false);
                },
                on_failure: (error) => {
                    console.error("Recordings request failed:", error);
                    setLoading(false);
                },
            });
        }
    };

    const handleDeviceSelect = (newDeviceId) => {
        setSelectedSessions(false);
        if (selectedDeviceId) {
            apiHandler.removeUpdatesCallback(selectedDeviceId);
        }
        setSelectedDeviceId(newDeviceId);
        loadRecordings(newDeviceId);

        // Call the VideoPlayer method for live view.
        if (videoPlayerRef.current) {
            videoPlayerRef.current.showLiveView(newDeviceId);
        }

        // Subscribe to updates for the new device
        apiHandler.addUpdatesCallback(newDeviceId, {
            on_update: (updateMsg) => {
                if (updateMsg.type === "recording_added") {
                    const recording = updateMsg.payload.recording;
                    addRecording(recording.device_id, recording, true);
                } else {
                    console.log("RecordingsTab - unrecognized update", updateMsg);
                }
            }
        });
    };

    const addRecording = (device_id, recording, toTheTop = true) => {
        if (device_id !== selectedDeviceId) {
            console.error('Recording does not belong to the selected device');
            return;
        }
        setRecordings((prevRecordings) => {
            return toTheTop
                ? [recording, ...prevRecordings]
                : [...prevRecordings, recording];
        });
        console.log(`Recording for device ${device_id} added ${toTheTop ? 'at the top' : 'at the bottom'}`);
    };

    // When a recording is selected by user
    const handlePlay = (recording) => {
        console.log("Playing recording:", recording);
        if (videoPlayerRef.current) {
            setSelectedRecordingId(recording.id);
            videoPlayerRef.current.playRecording(recording.device_id, recording);
        }
    };

    // When a recording was switched by media server due to continuous playback
    const handleRecordingSwitched = (new_rec_id) => {
        setSelectedRecordingId(new_rec_id);
    };

    const selectPreviousRecording = () => {
        if (selectedRecordingId) {
            const currentIndex = recordings.findIndex(r => r.id === selectedRecordingId);
            const previousIndex = currentIndex + 1;
            if (previousIndex < recordings.length && previousIndex >= 0) {
                let previousRecording = recordings[previousIndex];
                handlePlay(previousRecording);
            } else {
                console.log("No previous recording available.");
            }
        }
    };

    const selectNextRecording = () => {
        if (selectedRecordingId) {
            const currentIndex = recordings.findIndex(r => r.id === selectedRecordingId);
            const nextIndex = currentIndex - 1;
            if (nextIndex < recordings.length && nextIndex >= 0) {
                let nextRecording = recordings[nextIndex];
                handlePlay(nextRecording);
            } else {
                console.log("No next recording available.");
            }
        }
    };

    const handleDelete = (recording) => {
        console.log("Deleting recording:", recording);
        removeRecording(recording.device_id, recording.id);
    };

    const removeRecording = (deviceId, recordingId) => {
        if (apiHandler) {
            apiHandler.sendRequest(
                { method: "api", type: "delete_recording", payload: { id: recordingId } },
                {
                    on_success: () => {
                        showRecordingRemoved(deviceId, recordingId);
                    },
                    on_failure: (error) => {
                        showRecordingRemovalError(deviceId, recordingId);
                        console.error("Deleting error:", error);
                    },
                }
            );
        }
    };

    const showRecordingRemoved = (deviceId, recordingId) => {
        setRecordings((prevRecordings) =>
            prevRecordings.filter((recording) => recording.id !== recordingId)
        );
        console.log(`Recording ${recordingId} removed successfully!`);
    };

    const showRecordingRemovalError = (deviceId, recordingId) => {
        console.log("Error removing recording: ", recordingId);
        if (snackbarRef.current) {
            alertRef.current.textContent = "Recording deletion failed";
            snackbarRef.current.show();
        }
    };

    const loadSessions = () => {
        if (apiHandler) {
            setLoading(true);
            const utcMillisNow = Date.now();
            const message = {
                method: 'api',
                type: 'get_all_sessions',
                payload: {
                    from_ms: 0,
                    to_ms: utcMillisNow,
                    limit: 20,
                },
            };

            apiHandler.sendRequest(message, {
                on_success: (payload) => {
                    console.log("Sessions loaded:", payload.sessions.length);
                    setSessions(payload.sessions);
                    setLoading(false);
                },
                on_failure: (error) => {
                    console.error("Sessions request failed:", error);
                    setLoading(false);
                },
            });
        }
    };

    const handleSessionsSelect = () => {
        // clear device selection
        if (selectedDeviceId) {
            apiHandler.removeUpdatesCallback(selectedDeviceId);
            if (videoPlayerRef.current) {
                videoPlayerRef.current.showLiveView(selectedDeviceId);
            }
            setSelectedDeviceId(null);
        }
        setSelectedSessions(true);
        loadSessions();
        // TODO: add subscription for new sessions if needed
    };

    return (
        <div style={{ height: '100%', aspectRatio: '29/12' }}>
            <Grid container spacing={2} sx={{ flex: 1, height: '100%', minHeight: 0 }}>
                {/* Cameras / Sessions Section */}
                <Grid item xs={2} sx={{ height: '100%', minHeight: 0, borderRadius: 'lg' }}>
                    <div style={{ height: '7%', marginLeft: 15 }}>
                        <Typography level="h4" sx={{ fontSize: 'xl', mb: 0.5, marginLeft: 2 }}>
                            Sessions
                        </Typography>
                    </div>

                    <Sheet
                        id="sessionsContainer"
                        sx={{
                            height: '10%',
                            minHeight: 0,
                            overflowY: 'auto',
                            borderRadius: 'lg',
                            paddingLeft: '30px',
                        }}
                    >
                        <div
                            onClick={handleSessionsSelect}
                            style={{
                                backgroundColor: selectedSessions ? '#333' : 'transparent',
                                padding: '10px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                color: selectedSessions ? '#fff' : '#000',
                            }}
                        >
                            All Sessions
                        </div>
                    </Sheet>

                    <div style={{ height: '7%', marginLeft: 15 }}>
                        <Typography level="h4" sx={{ fontSize: 'xl', mb: 0.5, marginLeft: 2 }}>
                            Cameras
                        </Typography>
                    </div>
                    <Sheet
                        id="camerasContainer"
                        sx={{
                            height: '95%',
                            minHeight: 0,
                            overflowY: 'auto',
                            borderRadius: 'lg',
                            paddingLeft: '30px',
                        }}
                    >
                        {devices.map((device) => (
                            <div
                                key={device.id}
                                onClick={() => handleDeviceSelect(device.id)}
                                style={{
                                    backgroundColor: device.id === selectedDeviceId ? '#333' : 'transparent',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    color: device.id === selectedDeviceId ? '#fff' : '#000',
                                }}
                            >
                                {device.name}
                            </div>
                        ))}
                        {devices.length === 0 && (
                            <div>No devices available</div>
                        )}
                    </Sheet>
                </Grid>

                {/* Video Section */}
                <Grid item xs={7} sx={{ height: '100%', minHeight: 0, bgcolor: 'background.level2', borderRadius: 'lg' }}>
                    <div style={{ width: '100%', height: '13.5%' }}></div>
                    <AspectRatio ratio="16/9" sx={{ width: '100%', height: '100%', borderRadius: 'lg' }}>
                        <VideoPlayer
                            ref={videoPlayerRef}
                            style={{ width: '100%', height: '100%', display: 'block' }}
                            onRecordingSwitched={handleRecordingSwitched}
                            onPrevious={selectPreviousRecording}
                            onNext={selectNextRecording}
                        />
                    </AspectRatio>
                </Grid>

                {/* Recordings / Sessions List Section */}
                <Grid item xs={3} sx={{ height: '100%', width: '25%', minHeight: 0, padding: 0 }}>
                    <div style={{ height: '15%', marginLeft: 15 }}>
                        <Typography level="h4" sx={{ fontSize: 'xl', mb: 0.5, marginLeft: 2 }}>
                            Feed
                        </Typography>
                        <DaysContainer style={{ width: '70%' }} />
                    </div>

                    <Sheet
                        id="recordingsContainer"
                        sx={{
                            height: '85%',
                            minHeight: 0,
                            overflowY: 'auto',
                            borderRadius: 'lg',
                            paddingLeft: '30px',
                        }}
                    >
                        {/* Conditionally render sessions or recordings */}
                        {selectedSessions ? (
                            sessions && sessions.length > 0 ? (
                                sessions.map((session) => (
                                    <SessionCard
                                        key={session.id}
                                        session={session}
                                        onPlay={handlePlay}
                                        onDelete={handleDelete}
                                        openMenu={openMenu}
                                        setOpenMenu={setOpenMenu}
                                    />
                                ))
                            ) : (
                                <div>No sessions available</div>
                            )
                        ) : (
                            recordings && recordings.length > 0 ? (
                                recordings.map((recording) => (
                                    <RecordingCard
                                        key={recording.id}
                                        recording={recording}
                                        onPlay={handlePlay}
                                        onDelete={handleDelete}
                                        openMenu={openMenu}
                                        setOpenMenu={setOpenMenu}
                                    />
                                ))
                            ) : (
                                <div>No recordings available</div>
                            )
                        )}
                    </Sheet>
                </Grid>
            </Grid>

            {/* Snackbar for error message */}
            <Snackbar
                ref={snackbarRef}
                autoHideDuration={4000}
                onClose={() => {}}
                open={false}
            >
                <Alert ref={alertRef} variant="filled" severity="error">
                    {/* Error message will be updated dynamically */}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default RecordingsTab;
